import { CloudHttpClient, type CloudHttpOptions } from "./cloud-http.ts";
import { OrniTransportError } from "./transport.ts";

export interface CloudDevice {
  deviceId: string;
  displayName: string;
  platform: string;
  architecture: string;
  capabilities: Record<string, unknown>;
  protocolMin: number | null;
  protocolMax: number | null;
  online: boolean;
  lastSeenAt: string | null;
  enrolledAt: string;
  terminalRelay?: DeviceTerminalRelayEndpoint;
}

export interface DeviceTerminalRelayEndpoint {
  routeId: string;
  epoch: number;
  host: string;
  relayUrl: string;
}

export interface DeviceMutationResult {
  deviceId: string;
  changed: boolean;
  revokedAt: string;
  /** Cloud structural-event cursor shared by every signed-in client. */
  revocationCursor: number | null;
}

/** Account-scoped device management. Runtime execution remains in Daemon. */
export class CloudDevicesClient {
  private readonly http: CloudHttpClient;
  private cache: { value: CloudDevice[]; expiresAt: number } | null = null;
  private requestTask: Promise<CloudDevice[]> | null = null;

  constructor(options: CloudHttpOptions) {
    this.http = new CloudHttpClient(options);
  }

  async list(options: { refresh?: boolean } = {}): Promise<CloudDevice[]> {
    if (!options.refresh && this.cache && this.cache.expiresAt > Date.now()) {
      return this.cache.value.slice();
    }
    if (this.requestTask) return this.requestTask.then((devices) => devices.slice());
    const task = (async () => {
      const value = await this.http.request("/v1/devices");
      if (!Array.isArray(value)) throw new OrniTransportError("CONTRACT_INVALID", "Cloud returned an invalid device list.");
      const devices = value.map(projectDevice);
      this.cache = { value: devices, expiresAt: Date.now() + 10_000 };
      return devices;
    })();
    this.requestTask = task;
    void task.finally(() => {
      if (this.requestTask === task) this.requestTask = null;
    }).catch(() => undefined);
    return task.then((devices) => devices.slice());
  }

  async revoke(deviceId: string): Promise<DeviceMutationResult> {
    if (!deviceId.trim()) throw new OrniTransportError("INVALID_ARGUMENT", "A device is required.");
    const value = record(await this.http.request(`/v1/devices/${encodeURIComponent(deviceId)}/revoke`, { method: "POST" }));
    this.cache = null;
    return {
      deviceId: text(value.deviceId),
      changed: value.changed === true,
      revokedAt: text(value.revokedAt),
      revocationCursor: nullableInteger(value.revocationCursor),
    };
  }
}

export function projectDeviceTerminalRelay(value: unknown): DeviceTerminalRelayEndpoint | undefined {
  if (value === undefined || value === null) return undefined;
  const row = record(value);
  const routeId = text(row.routeId);
  const epoch = Number.isSafeInteger(row.epoch) && Number(row.epoch) > 0
    ? Number(row.epoch)
    : null;
  const host = text(row.host).toLowerCase();
  const relayUrl = text(row.relayUrl);
  let parsed: URL;
  try { parsed = new URL(relayUrl); } catch {
    throw new OrniTransportError("CONTRACT_INVALID", "Cloud returned an invalid fixed terminal relay endpoint.");
  }
  const label = host.endsWith(".orni.app") ? host.slice(0, -".orni.app".length) : "";
  if (!epoch
    || !/^[a-z0-9]{18}$/.test(label)
    || parsed.protocol !== "wss:"
    || parsed.hostname !== host
    || parsed.port
    || parsed.pathname !== "/v1/socket"
    || parsed.search
    || parsed.hash
    || parsed.username
    || parsed.password) {
    throw new OrniTransportError("CONTRACT_INVALID", "Cloud returned an invalid fixed terminal relay endpoint.");
  }
  return { routeId, epoch, host, relayUrl };
}

function projectDevice(value: unknown): CloudDevice {
  const row = record(value);
  const terminalRelay = projectDeviceTerminalRelay(row.terminalRelay);
  return {
    deviceId: text(row.deviceId),
    displayName: textOr(row.displayName, text(row.deviceId)),
    platform: textOr(row.platform, "unknown"),
    architecture: textOr(row.architecture, "unknown"),
    capabilities: record(row.capabilities ?? {}),
    protocolMin: nullableInteger(row.protocolMin),
    protocolMax: nullableInteger(row.protocolMax),
    online: row.online === true,
    lastSeenAt: nullableText(row.lastSeenAt),
    enrolledAt: text(row.enrolledAt),
    ...(terminalRelay ? { terminalRelay } : {}),
  };
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new OrniTransportError("CONTRACT_INVALID", "Cloud returned an invalid device record.");
  }
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) throw new OrniTransportError("CONTRACT_INVALID", "Cloud device data omitted a required value.");
  return value;
}

function textOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function nullableText(value: unknown): string | null {
  return value === null || value === undefined ? null : text(value);
}

function nullableInteger(value: unknown): number | null {
  return value === null || value === undefined ? null : Number.isSafeInteger(value) ? Number(value) : null;
}
